<template>
    <div class="locations-map">
        <div class="locations-map--anchor" ref="anchor"/>
        <div class="locations-map--row grid--row">
            <div class="grid--col">
                <h3 class="locations-map--title u-reset">
                    {{ title }}
                </h3>
            </div>
        </div>
        <div class="locations-map--row grid--row">
            <div class="locations-map--map-col grid--col">
                <div class="locations-map--filters">
                    <ui-select :value="safeActiveFilter.value" @change="onSelectChanged">
                        <span slot="label">
                            {{ selectLabel }}
                        </span>
                        <ui-option :value="option.value" v-for="option in locationFilters" :key="option.value">
                            {{ option.label }}
                        </ui-option>
                    </ui-select>
                </div>
                <figure class="locations-map--map">
                    <svg class="locations-map--svg"
                            preserveAspectRatio="xMidYMid meet"
                            width="784"
                            height="496"
                            viewBox="0 0 784 496"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                        <!--
                       <g>
                            <text :x="location.x"
                                    :y="location.y"
                                    class="locations-map&#45;&#45;text"
                                    v-for="(location, index) in locationCoordinates"
                                    :key="`text-${index}`">{{ location.id || 'noop' }}
                            </text>
                        </g>
                        -->
                        <circle :cx="location.x"
                                :cy="location.y"
                                r="5"
                                class="locations-map--circle"
                                :class="[bemAdd(safeActiveFilter.value === location.id ? 'active' : 'base', 'circle')]"
                                v-for="location in locationCoordinates"
                                :key="location.id"
                                @click="onPinClicked(location.id)"/>
                    </svg>
                </figure>
            </div>
        </div>
        <ul class="locations-map--row grid--row u-reset">
            <li class="locations-map--item grid--col" v-for="(location, index) in filteredLocations" :key="index">
                <picture-element class="locations-map--item-image" v-bind="location.image"/>
                <h4 class="locations-map--item-title u-reset">
                    {{ location.name }}
                </h4>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <p class="locations-map--address" v-html="location.address"/>

                <ul class="locations-map--item-contact-info u-reset">
                    <li v-if="location.phone">
                        <a class="locations-map--item-link u-reset" :href="`tel:${location.phone}`">
                            <ui-icon symbol="phone" size="medium"/>
                            <span class="locations-map--item-link-text">
                                {{ location.phone }}
                            </span>
                        </a>
                    </li>
                    <li v-if="location.email">
                        <a class="locations-map--item-link u-reset" :href="`mailto:${location.mail}`">
                            <ui-icon symbol="mail" size="medium"/>
                            <span class="locations-map--item-link-text">
                                {{ location.email }}
                            </span>
                        </a>
                    </li>
                    <li v-if="location.directions">
                        <a class="locations-map--item-link u-reset"
                                :href="location.directions"
                                target="_blank"
                                rel="noopener noreferrer">
                            <ui-icon symbol="pin" size="medium"/>
                            <span class="locations-map--item-link-text">
                                {{ directionsLabel }}
                            </span>
                        </a>
                    </li>
                </ul>
                <p v-if="location.activities">
                    {{ location.activities }}
                </p>
            </li>
        </ul>
    </div>
</template>

<script>
    import UiIcon from '@dreipol/vue-ui/src/components/icon/icon.vue';
    import UiSelect from '@dreipol/vue-ui/src/components/form/select/select.vue';
    import UiOption from '@dreipol/vue-ui/src/components/form/option/option.vue';
    import locationCoordinates from './location-coordinates.json';
    import { first } from 'lodash/fp';
    import { bemMixin } from '@dreipol/vue-ui/src/mixins';
    import PictureElement from '../picture-element/picture-element';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        components: {
            PictureElement,
            UiSelect,
            UiOption,
            UiIcon,
        },
        mixins: [
            bemMixin('locations-map'),
        ],
        props: {
            title: {
                type: String,
                required: true,
            },
            directionsLabel: {
                type: String,
                required: true,
            },
            initialActiveFilter: {
                type: String,
                default: '',
            },
            locationFilters: {
                type: Array,
                required: true,
            },
            locationItems: {
                type: Array,
                required: true,
            },
            selectLabel: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                activeFilter: '',
            };
        },
        computed: {
            ...mapGetters('user-region', ['getSafelyUserRegion']),
            currentRegion() {
                return this.getSafelyUserRegion(this.locationItems.map(({ regionID }) => regionID));
            },
            safeActiveFilter() {
                const matchedFilter = this.locationFilters.find(filter => filter.value === this.activeFilter);

                // make always sure that the filters values set are included in the this.locationFilters collection
                return matchedFilter ? matchedFilter : first(this.locationFilters);
            },
            locationCoordinates() {
                return locationCoordinates.sort(c => c.id === this.safeActiveFilter.value ? 1 : 0);
            },
            filteredLocations() {
                return this.locationItems.filter(location => location.regionID === this.safeActiveFilter.value);
            },
        },
        methods: {
            ...mapActions('user-region', ['setUserRegion']),
            onSelectChanged({ target }) {
                const { value } = target;
                this.activeFilter = value;

                // this method call doesn't make much sense here, this module was not thought to be used in this way
                // that's a client request so please forgive me for this chaos
                this.setUserRegion({
                    region: value,
                });
            },
            onPinClicked(value) {
                this.activeFilter = value;

                // this method call doesn't make much sense here, this module was not thought to be used in this way
                // that's a client request so please forgive me for this chaos
                this.setUserRegion({
                    region: value,
                });
            },
        },
        mounted() {
            // get the initial active filter and otherwise fall back to the one saved in the session
            // (this module was not thought to be working in this way, that's a client request)
            this.activeFilter = this.initialActiveFilter || this.currentRegion;

            // scroll in to view only if the initial active filter will be passed as get param
            if (this.initialActiveFilter) {
                this.$refs.anchor.scrollIntoView();
            }
        },
    };
</script>
